import { combineReducers, Dispatch, Reducer, Store } from 'redux';
import { Saga, SagaMiddleware, Task } from '@redux-saga/core';

export type SagaInjector = (key: string, saga: Saga) => Task;

const createSagaInjector = (middleware: SagaMiddleware): SagaInjector => {
  const injectedSagas: { [key: string]: Task } = {};

  return (key, saga) => {
    if (injectedSagas[key]) {
      // You can cancel this task by task.cancel()
      return injectedSagas[key];
    }
    const task = middleware.run(saga);
    injectedSagas[key] = task;
    return task;
  };
};

export type ReducerInjector = (key: string, reducer: Reducer<any, any>) => void;

const createReducerInjector = <StoreType extends Store>(
  store: StoreType,
  reducers: { [key: string]: Reducer<any, any> },
): ReducerInjector => {
  const injectedReducers: typeof reducers = {};

  return (key, reducer) => {
    injectedReducers[key] = reducer;
    store.replaceReducer(combineReducers({ ...reducers, ...injectedReducers }));
  };
};

export interface IReduxReducerSagaController {
  replaceReducer: (nextReducer: Reducer) => void;
  injectReducer: ReducerInjector;
  injectSaga: SagaInjector;
  runSaga: (saga: Saga) => Task;
  dispatch: Dispatch;
}

const ReduxReducerSagaController = <StoreType extends Store<any, any>>(
  store: StoreType,
  reducers: { [key: string]: Reducer<any, any> },
  sagaMiddleware: SagaMiddleware,
): IReduxReducerSagaController => ({
  injectReducer: createReducerInjector(store, reducers),
  injectSaga: createSagaInjector(sagaMiddleware),
  runSaga: sagaMiddleware.run,
  replaceReducer: store.replaceReducer,
  dispatch: store.dispatch,
});

export default ReduxReducerSagaController;
